import {Component, EventEmitter, Input, Output, ChangeDetectorRef, Inject} from '@angular/core';

import { FileRecordObject, FileRecordError } from './file-record';
import { NmdpWidget, NMDPHttpClientInterceptor } from '@nmdp/nmdp-login';
import { AppComponent } from '../app.component';

let statusMessageMap: Map<string, string> = new Map<string, string>();
statusMessageMap.set('Uploaded.Success', 'File Upload Successful');
statusMessageMap.set('Uploaded.Failed', 'File Upload Failed');
statusMessageMap.set('Converted.Rejected', 'Converted HML file rejected by user');
statusMessageMap.set('Validated.Success', 'Valid NMDP HML format');
statusMessageMap.set('Submitted.Success', 'File was submitted to NMDP');


const UNCATEGORIZED     = 0;
const CONVERTIBLE_CSV   = 1;
const FAILED_CONVERSION = 6;
const VALIDATABLE_HML   = 2;
const FAILED_VALIDATION = 3;
const SUBMITTABLE_HML   = 4;
const SUBMITTED_HML     = 5;
const FAILED_SUBMISSION = 7;

const STATUS_SUCCESS = 'Success';
const STATUS_FAILED = 'Failed';
const STATUS_PENDING = 'Pending';

const EVENT_PROCESSING = 'Processing';
const EVENT_UPLOADED = 'Uploaded';
const EVENT_VALIDATED = 'Validated';
const EVENT_SUBMITTED = 'Submitted';

@Component({
       selector: '[file-record]',
    templateUrl: './file-record.component.html',
      styleUrls: [ './file-record.component.css' ]
})
export class FileRecordComponent {

    // data-table listens to these emitters
    // and makes function calls appropriately
    @Output() evtCsvConversion: EventEmitter<any> = new EventEmitter();
    @Output() evtValOrSubmission: EventEmitter<any> = new EventEmitter();
    @Output() evtValErrors: EventEmitter<any> = new EventEmitter();
    @Output() evtSubmision: EventEmitter<any> = new EventEmitter();
    @Output() evtViewFile: EventEmitter<any> = new EventEmitter();

    @Input('file-record') fileRecord: FileRecordObject;
    @Input('scope') scope: string; // 'active' or 'previous'

    // Indicates that a recently taken action is pending completion.
    // actionPending is true if the user has taken an action, but the back end
    // has not yet completed the task. This should affect the behavior of the
    // front end, for example by showing a progress indicator.
    public actionPending = false;

    private readonly VIEW_ERRORS: string = 'View Error(s)';

    // These are the text values of the <th> column header cells in the table
    public static headers(): string[] {
        return ['Filename',
            'Status',
            'Uploaded',
            'HML ID',
            'Actions'];
    }

    // Relative widths (percentage) of the table columns.
    // i.e. <col style="width: 23%;">
    // (The sum of the values of the returned array must be 100 percent)
    public static columnWidths(): number[] {
        return [23, 37, 11, 16, 13];
    }

    constructor(@Inject(NmdpWidget) private readonly loginWidget: NmdpWidget,
            private ref: ChangeDetectorRef,
            private appComponent: AppComponent) {}


    get fileName(): string {
        return this.fileRecord.fileName;
    }

    get statusMessage(): string {
        if (this.category === FAILED_VALIDATION || this.category === FAILED_CONVERSION || this.category === FAILED_SUBMISSION) {
            if (this.errors && this.errors.length === 0) {
                // "shouldn't happen"
                return '';
            }
          if (this.errors && this.errors.length === 1) {
                return this.errors[0].errorMessage;
            }
            return 'File contains HML error. ' +
                'Edit original file and upload again';
        }
        if (this.category === VALIDATABLE_HML && this.fileRecord.referenceUploadFileIid > 0) {
            return 'CSV successfully converted to HML';
        }
        // Default
        return statusMessageMap.get(this.fileRecord.currentEvent + '.' + this.fileRecord.status);
    }

    get hmlId(): string {
        return this.fileRecord.hmlId;
    }

    get uploaded(): string {
        return this.fileRecord.eventDate;
    }

    // The text of the action as presented to the user
    get actionString(): string {
        if (this.scope === 'previous') {
            switch (this.category) {
                case SUBMITTED_HML:
                    return 'View File';
                case FAILED_VALIDATION:
                    if (this.errors && this.errors.length > 1) {
                        return 'View Errors (' + this.errors.length + ')';
                    }
                    return this.VIEW_ERRORS;
                case FAILED_SUBMISSION:
                    if (this.errors && this.errors.length > 1) {
                        return 'View Errors (' + this.errors.length + ')';
                    }
                    return this.VIEW_ERRORS;
                default:
                    return '';
            }
        }
        if (!this.isActionPending()) {
            switch (this.category) {
                case CONVERTIBLE_CSV:
                    return 'Convert';
                case VALIDATABLE_HML:
                    return 'Validate';
                case FAILED_VALIDATION:
                    if (this.errors && this.errors.length > 1) {
                        return 'View Errors (' + this.errors.length + ')';
                    }
                    return this.VIEW_ERRORS;
                case SUBMITTABLE_HML:
                    return !this.appComponent.guest ? 'Submit' : '';
                case SUBMITTED_HML:
                    return !this.appComponent.guest ? 'View File' : '';
                case FAILED_SUBMISSION:
                    if (this.errors && this.errors.length > 1) {
                        return 'View Errors (' + this.errors.length + ')';
                    }
                    return this.VIEW_ERRORS;
                default:
                    return '';
            }
        } else {
          return '';
        }
    }

    // Different actions require varying presentations.  This function returns
    // the CSS class(es) for each action cell.
    get actionCss(): string {
        // No actions should be available while another is pending completion
        if (!this.isActionPending()) {
            switch (this.category) {
                case CONVERTIBLE_CSV:
                    return 'session-item-action';
                case VALIDATABLE_HML:
                    return 'session-item-action';
                case FAILED_VALIDATION:
                    return 'session-item-action session-item-warn';
                case SUBMITTABLE_HML:
                    return this.loginWidget.lastName !== 'UploaderGuest' ? 'session-item-action' : '';
                case SUBMITTED_HML:
                    return this.loginWidget.lastName !== 'UploaderGuest' ? 'session-item-action' : '';
                case FAILED_SUBMISSION:
                    return 'session-item-action session-item-warn';
                default:
                    return '';
            }
        } else {
            return '';
        }
    }


    // This defines the function to be called for each action.
    public executeAction(): void {
        // No actions should be available while another is pending completion
        if (!this.isActionPending()) {
            switch (this.category) {
                case CONVERTIBLE_CSV:
                    this.evtCsvConversion.emit(this);
                    break;
                case VALIDATABLE_HML:
                    this.evtValOrSubmission.emit(this);
                    break;
                case FAILED_VALIDATION:
                case FAILED_SUBMISSION:
                    this.evtValErrors.emit(this.fileRecord);
                    break;
                case SUBMITTABLE_HML:
                    if (this.loginWidget.lastName !== 'UploaderGuest') {
                        this.evtSubmision.emit(this);
                    }
                    break;
                case SUBMITTED_HML:
                    if (this.loginWidget.lastName !== 'UploaderGuest') {
                        this.evtViewFile.emit(this.fileRecord.docGuid);
                    }
                    break;
                default:
                    // no action
            }
        }
    }

    // "Category" of the file record.  This will dictate what actions can be
    // taken by the user as well as the appearance of on-screen items.
    get category(): number {
        let fileType     = this.fileRecord.fileType;
        let currentEvent = this.fileRecord.currentEvent;
        let status       = this.fileRecord.status;
        if (fileType === 'csv') {
                if (status === STATUS_SUCCESS) {
                    return CONVERTIBLE_CSV;
                } else {
                    return FAILED_CONVERSION;
                }
        }
        if (fileType === 'hml') {
            if (currentEvent === EVENT_UPLOADED && status === STATUS_SUCCESS) {
                return VALIDATABLE_HML;
            }
            if (currentEvent === EVENT_VALIDATED) {
                if (status === STATUS_SUCCESS) {
                    return SUBMITTABLE_HML;
                } else {
                    return FAILED_VALIDATION;
                }
            }
            if (currentEvent === EVENT_SUBMITTED) {
                if (status === STATUS_SUCCESS) {
                    return SUBMITTED_HML;
                } else {
                    return FAILED_SUBMISSION;
                }
            }
        }
        return UNCATEGORIZED;
    }

    get errors(): FileRecordError[] {
        return this.fileRecord.errors;
    }

    public isActionPending(): boolean {

      if (this.fileRecord.currentEvent === EVENT_PROCESSING || this.actionPending) {
        return true;
      } else {
        return false;
      }
    }
}
